import React = require("react");
import { QuestionListItem } from "./QuestionListItem";
import { useDraggable, useDroppable } from "@dnd-kit/core";
import { SyntheticListenerMap } from "@dnd-kit/core/dist/hooks/utilities";
import styled from "styled-components";
import { Survey, SurveyTheme, LanguageCode, getSurveyStatus, surveyHelper, fieldValidation, SurveyQuestion } from "../../../../../../../amorg-common";
import { updateSurveyThemeText, deleteSurveyTheme, moveSurveyTheme } from "../../../../../../../api/surveyClientApi";
import { Button } from "../../../../../../../designSystem/button/Button";
import { Card } from "../../../../../../../designSystem/Card";
import { AnimatedCollapsibleContent } from "../../../../../../../designSystem/collapsible/AnimatedCollapsibleContent";
import { rawColors, colors } from "../../../../../../../designSystem/colors/color";
import { EditableText } from "../../../../../../../designSystem/compositeComponents/EditableText";
import { Flex, FlexItem } from "../../../../../../../designSystem/compositeComponents/FlexHelpers";
import { PopupMenuButton } from "../../../../../../../designSystem/compositeComponents/PopupMenuButton";
import { DragButton } from "../../../../../../../designSystem/dragndrop/DragButton";
import { QuestionConditionsModal } from "./QuestionConditionsModal";
import { EditQuestionEditModal, NewQuestionEditModal } from "./QuestionEditModal";

//type InEditQuestionState = { questionIndex:number, question:SurveyQuestion };

type ThemedQuestionListItemProps = {
    survey: Survey,
    themes: SurveyTheme[],
    theme: SurveyTheme,
    themeIndex: number,
    allIndicators: string[]
    languageCode:LanguageCode,
    onSurveyUpdated: () => void
}


export function ThemedQuestionList(props: ThemedQuestionListItemProps): JSX.Element 
{
    const [expanded, setExpanded] = React.useState(true);
    
    const [newQuestionModalLauncher, setShowNewQuestionModalLauncher] = React.useState<{ themeIndex: number } | null>(null);
    const [editQuestionModalLauncher, setShowEditQuestionModalLauncher] = React.useState<{ themeIndex: number, questionIndex: number } | null>(null);
    const [editQuestionConditionsModalLauncher, setEditQuestionConditionsModalLauncher] = React.useState<{ themeIndex: number, questionIndex: number } | null>(null);
    
    const { isOver: isThemeOver, setNodeRef: setThemeDroppableNodeRef } = useDroppable({
        id: `theme_${props.themeIndex}`,
    });
    const { attributes, listeners, setNodeRef: setThemeDraggableNodeRef, setActivatorNodeRef, transform } = useDraggable({
        id: `theme_${props.themeIndex}`,
    });
    const { isOver: isQuestionListOver, setNodeRef: setQuestionListNodeRef } = useDroppable({
        id: `theme_questions_${props.themeIndex}`,
    });

    const draggableStyle = transform ? {
        transform: `translate3d(${transform.x}px, ${transform.y}px, 0)`,
    } : undefined;

    async function handleThemeSave(newValue: string) {
        if ((await updateSurveyThemeText(props.survey.id, props.themeIndex, props.languageCode, newValue)).isSuccess){
            props.onSurveyUpdated();
        }
    }

    function handleClickAddNewQuestion() {
        setShowNewQuestionModalLauncher({ themeIndex:props.themeIndex });
    }

    function handleOpenEditQuestion(relativeQuestionIndex:number)
    {
        setShowEditQuestionModalLauncher({ themeIndex: props.themeIndex, questionIndex: relativeQuestionIndex });
    }

    function handleOpenConditionsModal(relativeQuestionIndex:number)
    {
        setEditQuestionConditionsModalLauncher({ themeIndex: props.themeIndex, questionIndex: relativeQuestionIndex });
    }

    async function handleClickDeleteTheme() {
        if (await deleteSurveyTheme(props.survey.id, props.themeIndex))
            props.onSurveyUpdated();
    }

    async function handleMoveTheme(move: 'up' | 'down') {
        if (move === 'up') {
            if (props.themeIndex > 0) {
                await moveSurveyTheme(props.survey.id, props.themeIndex, props.themeIndex - 1);
                props.onSurveyUpdated();
            }
        }
        if (move === 'down') {
            if (props.themeIndex < props.survey.themes.length - 1) {
                await moveSurveyTheme(props.survey.id, props.themeIndex, props.themeIndex + 1);
                props.onSurveyUpdated();
            }
        }
    }

    const disableEdit = !getSurveyStatus(props.survey).canEdit;
    const isEmptyTheme = props.theme.questions.length === 0;

    const renderQuestions:{surveyQuestion:SurveyQuestion, index:number}[] = props.theme.questions
        .map((val, index) => ({ surveyQuestion: val, index }))
        .filter(({ surveyQuestion }) => surveyQuestion.conditionalDisplay === undefined);

    return (
        <Flex flexDirection="column" gap={8}>
            {/* <PrettyPrintJson data={props.theme.questions} /> */}
            <div
                ref={(node) => {
                    setThemeDroppableNodeRef(node);
                    setThemeDraggableNodeRef(node);
                }}
                {...attributes}
                style={{
                    ...draggableStyle,
                    backgroundColor: isThemeOver ? '#f0f0f0' : undefined,
                }}>
                <Card variant="outlined" padding="16px 4px" >
                        <Flex justifyContent="space-between" gap={0} alignItems="center">
                            
                            <FlexItem flexGrow="1">
                                <Flex alignItems="center" gap={8}>
                                    
                                    {!disableEdit &&
                                        <DragButton listeners={listeners as SyntheticListenerMap} setActivatorNodeRef={setActivatorNodeRef} />
                                    }

                                <Button
                                    variant="text"
                                    onClick={() => setExpanded(!expanded)}
                                    icon={ expanded ? 'arrows_chevron_chevronUp' : 'arrows_chevron_chevronDown' } />

                                <EditableText
                                    styleMode='H2'
                                    ariaLabel="Spørgsmålstema"
                                    placeholder="Tema"
                                    value={surveyHelper.getThemeValue(props.survey, props.themeIndex, props.languageCode)}
                                    validator={(currentValue) =>
                                        fieldValidation.surveyTheme.name(currentValue)
                                                ? { valid:true }
                                                : { valid:false, errorMessage:`Ugyldig tekst. Skal udfyldes med maks. 200 (${currentValue.length}) tegn.`}
                                    }
                                    onSave={(value, isValid) => {
                                        if(isValid)
                                            handleThemeSave(value);
                                    }}
                                    disabled={disableEdit} />
                                </Flex>
                            </FlexItem>

                            <PopupMenuButton
                                buttonIcon="navigation_context"
                                entries={[
                                    {   type: 'menuItem',
                                        text: 'Flyt tema op',
                                        icon: 'arrows_chevron_chevronUp',
                                        onClick: () => handleMoveTheme('up'),
                                        disabled: disableEdit
                                    },
                                    {   type: 'menuItem',
                                        text: 'Flyt tema ned',
                                        icon: 'arrows_chevron_chevronDown',
                                        onClick: () => handleMoveTheme('down'),
                                        disabled: disableEdit
                                    },
                                    {
                                        type: 'menuItem',
                                        text: 'Slet tema',
                                        icon: 'interface_actions_delete',
                                        onClick: handleClickDeleteTheme,
                                        disabled: disableEdit
                                    }
                                ]}
                            />                        
                        </Flex>
                </Card>
            </div>
            <AnimatedCollapsibleContent open={expanded}>
                <Flex flexDirection="column" gap={8}>
                    <Card variant="lightGray">
                        <Flex
                            flexDirection="column"
                            gap={2}
                            ref={setQuestionListNodeRef}
                            style={{
                                backgroundColor: isQuestionListOver ? '#f0f0f0' : undefined,
                                minHeight: isEmptyTheme ? '64px' : '8px',
                            }}>
                            {isEmptyTheme &&
                                <EmptyQuestionDropZone isActive={isQuestionListOver}>
                                    Ingen spørgsmål oprettet
                                </EmptyQuestionDropZone>
                            }


                            {renderQuestions.map((q) =>
                            {
                                const conditionalQuestionIndices:number[] = [];
                                for(let themeQuestionIndex = q.index + 1; themeQuestionIndex < props.theme.questions.length; themeQuestionIndex++)
                                {
                                    if(props.theme.questions[themeQuestionIndex].conditionalDisplay !== undefined)
                                        conditionalQuestionIndices.push(themeQuestionIndex);
                                    else
                                        break;
                                }

                                return (
                                    <QuestionListItem key={q.index}
                                        survey={props.survey}
                                        languageCode={props.languageCode}
                                        allIndicators={props.allIndicators}
                                        absoluteQuestionIndex={surveyHelper.getQuestionIndex(props.survey, props.themeIndex, q.index)}
                                        themeIndex={props.themeIndex}
                                        themeQuestionIndex={q.index}
                                        conditionalQuestionIndices={conditionalQuestionIndices}
                                        disableEdit={disableEdit}
                                        onQuestionUpdated={props.onSurveyUpdated}
                                        onOpenEditModal={(index) => handleOpenEditQuestion(index)}
                                        onOpenConditionsModal={(index) => handleOpenConditionsModal(index)}
                                    />
                                );
                            })}


                        </Flex>
                    </Card>
                    <Flex justifyContent="flex-end">
                        <Button variant="text" text="Tilføj spørgsmål" icon="actions_plus" onClick={handleClickAddNewQuestion} disabled={disableEdit} />
                    </Flex>
                </Flex>
            </AnimatedCollapsibleContent>

            {editQuestionModalLauncher &&
                <EditQuestionEditModal
                    showModal={true}
                    themeIndex={editQuestionModalLauncher.themeIndex}
                    questionIndex={editQuestionModalLauncher.questionIndex}
                    selectedLanguageCode={props.languageCode}
                    survey={props.survey}
                    onSurveyUpdated={() => {
                        props.onSurveyUpdated();
                        setShowEditQuestionModalLauncher(null);
                    }}
                    onClose={() => setShowEditQuestionModalLauncher(null)}
                />
            }

            {newQuestionModalLauncher &&
                <NewQuestionEditModal
                    showModal={true}
                    themeIndex={newQuestionModalLauncher.themeIndex}
                    survey={props.survey}
                    onSurveyUpdated={() => {
                        props.onSurveyUpdated();
                        setShowNewQuestionModalLauncher(null);
                    }}
                    onClose={() => setShowNewQuestionModalLauncher(null)}
                />
            }

            {editQuestionConditionsModalLauncher &&
                <QuestionConditionsModal
                    survey={props.survey}
                    questionIdentifier={editQuestionConditionsModalLauncher}
                    onQuestionUpdated={() => {
                        props.onSurveyUpdated();
                        setEditQuestionConditionsModalLauncher(null);
                    }}
                    onClose={() => setEditQuestionConditionsModalLauncher(null)}
                />
            }

        </Flex> 
    );
}

const EmptyQuestionDropZone = styled.div<{ isActive:boolean }>`
    min-height: 64px;
    border: 1px dashed ${rawColors.gray.light};
    border-radius: 6px;
    display: flex;
    align-items: center;
    justify-content: center;
    color: ${colors.text.muted};
    background-color: ${props => props.isActive ? '#f0f0f0' : 'transparent'};
`;